/**
 * Acknowledge state for the SCREEN-OFF (background) alarm notifications.
 *
 * The background runner evaluates the priority alarms on every fix and, while a
 * condition holds, would re-post the same loud notification each time. That's the
 * bug behind "pressing OK does nothing": the notification the crew dismissed comes
 * straight back on the next fix, so it can never be cleared. There was also no
 * handler wired to the notification's action at all, so a tap changed no state.
 *
 * This is the small pure state machine that fixes it. It tracks each alarm as an
 * "episode": an episode begins the moment the alarm starts firing and ends when it
 * stops. Acknowledging silences the CURRENT episode, so the popup closes and stays
 * closed while that same condition persists. It re-alerts on a fresh episode: for
 * the anchor drag alarm that means the boat came back inside the watch boundary and
 * then dragged out again. Crucially, nothing here touches the anchor-watch arming,
 * so acknowledging a drag alert silences the alert without standing the watch down.
 *
 * Pure and framework-free so it's unit-testable without mounting the runner or the
 * notification plugin. The runner holds one AlarmAckState, feeds each fix through
 * advanceAlarms, and calls acknowledgeAlarm from the notification-action listener.
 */

/** Per-alarm episode bookkeeping. */
export interface AlarmEpisode {
  /**
   * Rising-edge counter: bumped by one each time the alarm transitions from
   * not-firing to firing. 0 means it has never fired in this session.
   */
  episodeId: number;
  /** Whether the alarm was firing at the last advance. */
  firing: boolean;
  /**
   * The episodeId the crew acknowledged, or null if the current episode is
   * un-acknowledged. Acked === current episodeId means "silence this one".
   */
  ackedEpisodeId: number | null;
  /**
   * Whether we believe a notification for this key is currently on the phone.
   *
   * Only here to stop us cancelling notifications that were never posted. The
   * idle branch used to clear every key on every fix, "defensively", and on a
   * quiet anchor watch every key is idle, so a night at anchor sent something
   * like a hundred thousand cancel calls across the Capacitor bridge for
   * notifications that did not exist (fb: frozen overnight). Every one of those
   * is a promise nobody awaits, and if the native side falls behind even
   * slightly the backlog only ever grows, until the bridge is too busy to carry
   * a tap through and the app looks locked solid.
   *
   * It starts TRUE on a key we have never seen, which keeps the defensive sweep
   * that was wanted in the first place: anything left on screen by a previous
   * run gets cleared on the first fix. Once. Not once a second.
   */
  posted: boolean;
}

/** The whole acknowledge snapshot, keyed by alarm key. */
export type AlarmAckState = Record<string, AlarmEpisode>;

/** What one advance decided the runner should do this fix. */
export interface AdvanceResult {
  /** The new state to carry to the next fix. */
  state: AlarmAckState;
  /** Keys to (re)post a loud notification for right now. */
  toNotify: string[];
  /** Keys whose notification should be cleared (resolved, so stop nagging). */
  toClear: string[];
}

/** A fresh, empty acknowledge state (nothing has fired yet). */
export function initAlarmAckState(): AlarmAckState {
  return {};
}

/** Read an episode, defaulting to the never-fired baseline. */
function episode(state: AlarmAckState, key: string): AlarmEpisode {
  // posted starts true so the first advance of a run sweeps up anything a
  // previous run left on the phone. See AlarmEpisode.posted.
  return state[key] ?? { episodeId: 0, firing: false, ackedEpisodeId: null, posted: true };
}

/** Whether the current episode of a key is the acknowledged one. */
function isAcknowledged(e: AlarmEpisode): boolean {
  return e.ackedEpisodeId !== null && e.ackedEpisodeId === e.episodeId;
}

/**
 * Fold one fix's firing set into the acknowledge state and decide what to post or
 * clear. `allKeys` is the fixed set of background alarm keys so a key that has
 * fallen silent still gets its notification cleared.
 *
 * Rules per key:
 *   - rising edge (now firing, wasn't): a new episode, so notify.
 *   - still firing (same episode): notify only if this episode isn't acknowledged.
 *     An acknowledged, still-firing alarm is left alone, so its popup stays closed.
 *   - falling edge (was firing, now not): the episode ends, so clear.
 *   - idle (not firing, wasn't): clear, so any stale notification can't linger.
 */
export function advanceAlarms(
  state: AlarmAckState,
  firingKeys: Iterable<string>,
  allKeys: readonly string[],
): AdvanceResult {
  const firing = new Set(firingKeys);
  const next: AlarmAckState = { ...state };
  const toNotify: string[] = [];
  const toClear: string[] = [];

  for (const key of allKeys) {
    const e = episode(state, key);
    const isFiring = firing.has(key);

    if (isFiring && !e.firing) {
      // Rising edge: start a new episode. A prior acknowledgement was for an older
      // episode, so this fresh one alerts.
      next[key] = {
        episodeId: e.episodeId + 1,
        firing: true,
        ackedEpisodeId: e.ackedEpisodeId,
        posted: true,
      };
      toNotify.push(key);
    } else if (isFiring && e.firing) {
      // Still firing: keep the episode, and re-post only while un-acknowledged.
      const notify = !isAcknowledged(e);
      next[key] = { ...e, posted: e.posted || notify };
      if (notify) toNotify.push(key);
    } else if (!isFiring && e.firing) {
      // Falling edge: the episode is over. Clear the notification.
      next[key] = { ...e, firing: false, posted: false };
      if (e.posted) toClear.push(key);
    } else {
      // Idle. Clear only if we think something is actually up there, which after
      // the first sweep of a run means never. Cancelling a notification that was
      // never posted is a no-op on the phone but NOT a no-op on the bridge, and
      // at one fix a second all night it is the difference between a quiet watch
      // and an app too busy to answer a tap.
      next[key] = { ...e, posted: false };
      if (e.posted) toClear.push(key);
    }
  }

  return { state: next, toNotify, toClear };
}

/**
 * Take everything down, for the master mute: the keys that need cancelling, and
 * the state to carry forward.
 *
 * The mute path had the same trouble the idle branch did. It cleared all four
 * keys on every fix for as long as the mute was on, so muting an alarm and
 * turning in for the night was another few hundred thousand cancel calls for
 * notifications that were already gone. Muting takes down whatever is up, once,
 * and after that there is nothing to take down.
 */
export function clearAllAlarms(
  state: AlarmAckState,
  allKeys: readonly string[],
): { state: AlarmAckState; toClear: string[] } {
  const next: AlarmAckState = { ...state };
  const toClear: string[] = [];
  for (const key of allKeys) {
    const e = episode(state, key);
    if (e.posted) toClear.push(key);
    next[key] = { ...e, firing: false, posted: false };
  }
  return { state: next, toClear };
}

/**
 * Acknowledge the current episode of an alarm: the crew pressed OK / dismissed its
 * notification. Marks this episode silenced so advanceAlarms won't re-post it while
 * the same condition holds. Only the one key is touched, and only its ack marker,
 * so the anchor watch (and every other alarm) stays exactly as armed as it was.
 *
 * Acknowledging a key that isn't currently firing simply marks its (idle) episode,
 * which the next rising edge supersedes, so it can never suppress a future alarm.
 */
export function acknowledgeAlarm(state: AlarmAckState, key: string): AlarmAckState {
  const e = episode(state, key);
  // Acknowledging takes the notification down with it (the runner cancels it on
  // the spot), so nothing is posted any more. Without this the falling edge
  // would later cancel a notification that went hours ago.
  return { ...state, [key]: { ...e, ackedEpisodeId: e.episodeId, posted: false } };
}
